import axios from 'axios';

// Use relative URL in production (served from same origin)
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token + active workspace to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const orgId = localStorage.getItem('activeOrgId');
  if (orgId) {
    config.headers['X-Org-Id'] = orgId;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (username, password) =>
    api.post('/api/auth/login', new URLSearchParams({ username, password }), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    }),

  getCurrentUser: () =>
    api.get('/api/auth/me'),

  verifyToken: () =>
    api.get('/api/auth/verify'),

  updateProfile: (userData) =>
    api.put('/api/auth/profile', userData),

  changePassword: (data) =>
    api.post('/api/auth/change-password', data),

  // Google OAuth
  getGoogleLoginUrl: (inviteToken) =>
    api.get('/api/auth/google/login', { params: inviteToken ? { invite_token: inviteToken } : {} }),

  googleTokenLogin: (idToken, inviteToken) =>
    api.post('/api/auth/google/token', { id_token: idToken, invite_token: inviteToken }),

  devLogin: (email, fullName, inviteToken) =>
    api.post('/api/auth/dev-login', { email, full_name: fullName, invite_token: inviteToken }),

  // Invite links
  inviteInfo: (token) =>
    api.get('/api/auth/invite-info', { params: { token } }),

  acceptInvite: (token) =>
    api.post('/api/auth/accept-invite', { token }),
};

// Organizations API
export const orgAPI = {
  listMine: () => api.get('/api/organizations'),
  create: (data) => api.post('/api/organizations', data),
  get: (id) => api.get(`/api/organizations/${id}`),
  update: (id, data) => api.put(`/api/organizations/${id}`, data),
  listWhitelist: (id) => api.get(`/api/organizations/${id}/whitelist`),
  addWhitelist: (id, value) => api.post(`/api/organizations/${id}/whitelist`, { value }),
  removeWhitelist: (id, entryId) => api.delete(`/api/organizations/${id}/whitelist/${entryId}`),
  listMembers: (id) => api.get(`/api/organizations/${id}/members`),
  invite: (id, email, role = 'member') => api.post(`/api/organizations/${id}/members`, { email, role }),
  removeMember: (id, userId) => api.delete(`/api/organizations/${id}/members/${userId}`),
  createInvite: (id, role = 'member') => api.post(`/api/organizations/${id}/invites`, { role }),
  listInvites: (id) => api.get(`/api/organizations/${id}/invites`),
  revokeInvite: (id, token) => api.delete(`/api/organizations/${id}/invites/${token}`),
  inboxOAuthConfig: (id) => api.get(`/api/organizations/${id}/inbox/oauth/config`),
};

// Investigations API
export const investigationAPI = {
  list: (sharedOnly) => api.get('/api/investigations', { params: { shared_only: !!sharedOnly } }),
  create: (data) => api.post('/api/investigations', data),
  get: (id) => api.get(`/api/investigations/${id}`),
  update: (id, data) => api.put(`/api/investigations/${id}`, data),
  delete: (id) => api.delete(`/api/investigations/${id}`),
  listRequests: (id) => api.get(`/api/investigations/${id}/requests`),
  attachRequest: (id, requestId) => api.post(`/api/investigations/${id}/requests/${requestId}`),
  detachRequest: (id, requestId) => api.delete(`/api/investigations/${id}/requests/${requestId}`),
  share: (id, payload) => api.post(`/api/investigations/${id}/shares`, payload),
  listShares: (id) => api.get(`/api/investigations/${id}/shares`),
  unshare: (id, userId) => api.delete(`/api/investigations/${id}/shares/${userId}`),
};

// PRA API
export const praAPI = {
  getDestinations: () =>
    api.get('/api/pra/destinations'),

  getSenderAccounts: () =>
    api.get('/api/pra/sender-accounts'),

  generateRequest: (requestData) =>
    api.post('/api/pra/generate', requestData),

  generateTopics: (requestData) =>
    api.post('/api/pra/generate-topics', requestData),

  combineTopics: (topics) =>
    api.post('/api/pra/combine-topics', { topics }),

  splitTopic: (topic, splitInstruction) =>
    api.post('/api/pra/split-topic', { topic, split_instruction: splitInstruction }),

  generateEmails: (requestData) =>
    api.post('/api/pra/generate-emails', requestData),

  createRequest: (requestData) =>
    api.post('/api/pra/requests', requestData),

  getRequests: (statusFilter, mineOnly, investigationId) =>
    api.get('/api/pra/requests', {
      params: { status_filter: statusFilter, mine_only: mineOnly, investigation_id: investigationId },
    }),

  getRequest: (id) =>
    api.get(`/api/pra/requests/${id}`),

  updateRequest: (id, data) =>
    api.put(`/api/pra/requests/${id}`, data),

  sendRequest: (id, senderAccountId) =>
    api.post(`/api/pra/requests/${id}/send`, null, {
      params: { sender_account_id: senderAccountId }
    }),

  submitViaNextRequest: (id, data) =>
    api.post(`/api/pra/requests/${id}/submit-nextrequest`, data),

  deleteRequest: (id) =>
    api.delete(`/api/pra/requests/${id}`),

  getDashboardStats: () =>
    api.get('/api/pra/dashboard/stats'),

  getRequestEmails: (id) =>
    api.get(`/api/pra/requests/${id}/emails`),

  attachEmailToRequest: (requestId, messageId, senderAccountId) =>
    api.post(`/api/pra/requests/${requestId}/emails/${messageId}`, null, {
      params: { sender_account_id: senderAccountId }
    }),

  detachEmailFromRequest: (requestId, messageId) =>
    api.delete(`/api/pra/requests/${requestId}/emails/${messageId}`),
};

// Admin API
export const adminAPI = {
  getUsers: () =>
    api.get('/api/admin/users'),

  createUser: (userData) =>
    api.post('/api/admin/users', userData),

  deleteUser: (id) =>
    api.delete(`/api/admin/users/${id}`),

  updateUser: (id, userData) =>
    api.put(`/api/admin/users/${id}`, userData),

  getEmailConfig: () =>
    api.get('/api/admin/email-config'),

  updateEmailConfig: (config) =>
    api.put('/api/admin/email-config', config),

  getAIBudget: () =>
    api.get('/api/admin/ai-budget'),

  getAIModels: () =>
    api.get('/api/admin/ai-models'),

  updateAIModel: (modelName) =>
    api.put(`/api/admin/ai-model?model_name=${modelName}`),

  // Destinations
  getDestinations: () =>
    api.get('/api/admin/destinations'),

  createDestination: (destinationData) =>
    api.post('/api/admin/destinations', destinationData),

  updateDestination: (id, destinationData) =>
    api.put(`/api/admin/destinations/${id}`, destinationData),

  deleteDestination: (id) =>
    api.delete(`/api/admin/destinations/${id}`),

  // Sender Accounts
  getSenderAccounts: () =>
    api.get('/api/admin/sender-accounts'),

  createSenderAccount: (accountData) =>
    api.post('/api/admin/sender-accounts', accountData),

  updateSenderAccount: (id, accountData) =>
    api.put(`/api/admin/sender-accounts/${id}`, accountData),

  deleteSenderAccount: (id) =>
    api.delete(`/api/admin/sender-accounts/${id}`),

  // Gmail OAuth
  getGmailOAuthConfig: () =>
    api.get('/api/admin/gmail/oauth/config'),
};

export default api;
